<template>
  <div>
    <SalePricesAdditionalFeeFilter
      :values="filterValues"
      :filter-submitting="isLoading"
      @filter="handleFilter"
    />

    <vue-good-table
      mode="remote"
      :sort-options="{ enabled: false }"
      :total-rows="totalRecords"
      :is-loading.sync="isLoading"
      :pagination-options="{
        enabled: true,
        jumpFirstOrLast: true,
        rowsPerPageLabel: t('Rows per page'),
        ofLabel: t('ofLabel'),
        nextLabel: t('nextLabel'),
        prevLabel: t('prevLabel'),
        dropdownAllowAll: false
      }"
      :show-footer="true"
      :rows="rows"
      :columns="getColumns"
      @on-page-change="onPageChange"
      @on-per-page-change="onPerPageChange"
    >
      <table-no-result-found slot="emptystate" />
      <template slot="table-row" slot-scope="props">
        <span v-if="props.column.field === 'providerName'">
          {{ props.row.providerName }}
        </span>
        <span v-if="props.column.field === 'customerName'">
          {{ props.row.customerName }}
        </span>
        <span v-if="props.column.field === 'description'">
          {{ props.row.description }}
        </span>
        <span v-if="props.column.field === 'priceType'">
          {{
            props.row.pricingType === 1
              ? $t('Fixed')
              : props.row.pricingType === 2
              ? $t('Percentage')
              : '-'
          }}
        </span>
        <span v-if="props.column.field === 'costPrice'">
          {{ props.row.price }}
        </span>
        <span v-if="props.column.field === 'salePrice'">
          {{ props.row.salePrice }}
        </span>
        <span v-if="props.column.field === 'status'">
          <b-badge :variant="getStatusVariant(props.row.status.value)" class="text-capitalize">
            {{ props.row.status.info }}
          </b-badge>
        </span>

        <span v-else-if="props.column.field === 'actions'">
          <span class="d-flex justify-content-center table-icon-buttons">
            <b-button
              :id="`edit-button-${props.row.id}`"
              variant="primary"
              type="button"
              size="sm"
              @click="detailFee(props.row.customerId, props.row.providerId)"
            >
              <feather-icon icon="Edit3Icon" size="15" />
              <b-tooltip
                triggers="hover"
                :target="`edit-button-${props.row.id}`"
                :title="t('Edit Additional Fee')"
                :delay="{ show: 0, hide: 0 }"
                boundary="viewport"
              />
            </b-button>
          </span>
        </span>
      </template>
    </vue-good-table>
  </div>
</template>

<script>
import { BTooltip, BButton, BBadge } from 'bootstrap-vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { VueGoodTable } from 'vue-good-table';
import { helper } from '@core/mixins/helper.js';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import useService from '@/auth/service/useService';
import SalePricesAdditionalFeeFilter from './SalePricesAdditionalFeeFilter.vue';

export default {
  components: {
    VueGoodTable,
    BTooltip,
    BButton,
    SalePricesAdditionalFeeFilter,
    BBadge,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent
  },

  mixins: [helper],

  data() {
    return {
      isLoading: false,
      rows: [],
      details: [],
      statusOptions: [],
      totalRecords: 0,
      filter: {
        page: 1,
        limit: 10
      },
      filterValues: {
        providerId: ''
      },
      filterSubmitting: false
    };
  },

  computed: {
    getColumns() {
      if (!this.rows.length) {
        return [];
      }

      return [
        {
          label: this.$t('Provider'),
          field: 'providerName'
        },
        {
          label: this.$t('Customer'),
          field: 'customerName'
        },
        {
          label: this.$t('Description'),
          field: 'description'
        },
        {
          label: this.$t('Pricing Type'),
          field: 'priceType'
        },
        {
          label: this.$t('Cost Price'),
          field: 'costPrice'
        },
        {
          label: this.$t('Sale Price'),
          field: 'salePrice'
        },
        {
          label: this.$t('Status'),
          field: 'status'
        },
        {
          label: this.$t('Actions'),
          field: 'actions'
        }
      ];
    }
  },

  async created() {
    this.loadItems();
  },

  methods: {
    getStatusVariant(status) {
      return status === 1 ? 'success' : 'danger';
    },

    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps };
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onPerPageChange(params) {
      this.filter.page = 1;
      this.updateParams({ limit: params.currentPerPage });
      this.loadItems();
    },

    async loadItems() {
      this.isLoading = true;

      const params = {
        ...this.filter,
        ...this.getFilteredObject(this.filterValues)
      };

      useService.pricingService
        .getAllSalePricesAdditionalFees(params)
        .then(async response => {
          if (this.statusOptions.length === 0) {
            const [statusResponse] = await Promise.all([this.getProviderAdditionalFeeStatus()]);

            this.statusOptions = statusResponse;
          }
          this.rows = response.data.map(item => ({
            ...item,
            status: this.statusOptions.find(status => status.value === item.status)
          }));
          this.totalRecords = response.meta.total;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    detailFee(customerId, providerId) {
      this.$router.push({
        name: 'sale-prices-additional-fees-form',
        params: { customerId, providerId }
      });
    },

    handleFilter(filters) {
      this.filterValues = filters;
      this.loadItems();
    }
  },

  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
